import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { useLocation, useParams } from "react-router";
import { meQuery } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { PortalNoAccess } from "../../portal/NoAccess.tsx";
import { isTeamMember, portalsInTeam } from "../../portal/standing.ts";
import { OutsideShell } from "../../routes/OutsideShell.tsx";
import { chunks } from "../../routes.tsx";
import { ShellSkeleton } from "../../skeletons/Skeletons.tsx";
import { WorkspaceShell } from "./WorkspaceShell.tsx";

const PortalShell = lazy(() => chunks.portalShell().then((module) => ({ default: module.PortalShell })));

/**
 * The layout route of `/{ws}` (028 FR-028-046, FR-028-058, `research.md` D-2831): which frame the
 * person works in, decided from `GET /me` before any read of the Team. A member of the Team, and
 * anyone `/me` cannot place, gets the workspace shell, whose own reads decide the rest, its no-access
 * page among them. A client member, holding no membership of the Team and a live membership of one of
 * its open portals, gets the portal shell, which draws the Tickets routes alone. A client member
 * whose every portal of the Team is closed, and a stranger at a Tickets route, get the portal's
 * no-access page, which names the Team by the address alone unless the person's closed portal names
 * it. A stranger anywhere else gets the workspace shell's no-access page as before.
 */
export function WorkspaceEntry() {
  const runtime = useRuntime();
  const { ws = "" } = useParams();
  const { pathname } = useLocation();
  const me = useQuery(meQuery(runtime));
  if (me.isPending) return <ShellSkeleton />;
  if (me.isError || isTeamMember(me.data, ws)) return <WorkspaceShell />;
  const held = portalsInTeam(me.data, ws);
  const open = held.filter((portal) => portal.status === "open");
  if (open.length > 0) {
    return (
      <Suspense fallback={<ShellSkeleton />}>
        <PortalShell ws={ws} me={me.data} portals={open} />
      </Suspense>
    );
  }
  const closed = held[0];
  if (closed !== undefined) {
    return (
      <OutsideShell>
        <PortalNoAccess kind="closed" team={closed.workspace.name} client={closed.account.name} />
      </OutsideShell>
    );
  }
  if (pathname === `/${ws}/tickets` || pathname.startsWith(`/${ws}/tickets/`)) {
    return (
      <OutsideShell>
        <PortalNoAccess kind="stranger" address={ws} />
      </OutsideShell>
    );
  }
  return <WorkspaceShell />;
}
